import type { CentralDashboardSummary } from "./central-dashboard";

export type CentralDashboardWorkItem = {
  actionType?: CentralDashboardSummary["attention"][number]["type"];
  assignedTo: string;
  caseName?: string;
  count: number;
  detail: string;
  href: string;
  id: string;
  sourceId?: string;
  receivedAt?: string | null;
  updatedAt?: string | null;
  dueAt?: string | null;
  version?: string;
  kind: "action" | "queue";
  priority: "Critical" | "High" | "Normal";
  title: string;
};

export function normalizeCentralDashboardSummary(response: Partial<CentralDashboardSummary> | Record<string, unknown>) {
  return {
    ...response,
    attention: Array.isArray(response.attention) ? response.attention : [],
    deliveryProviders: Array.isArray(response.deliveryProviders) ? response.deliveryProviders : [],
    familyFollowUps: Array.isArray(response.familyFollowUps) ? response.familyFollowUps : [],
    nextActionQueues: Array.isArray(response.nextActionQueues) ? response.nextActionQueues : [],
    priorityCases: Array.isArray(response.priorityCases) ? response.priorityCases : [],
    recentActivity: Array.isArray(response.recentActivity) ? response.recentActivity : [],
    reportLinks: Array.isArray(response.reportLinks) ? response.reportLinks : [],
  } as CentralDashboardSummary;
}

export function getCentralDeliveryProviderPresentation(provider: CentralDashboardSummary["deliveryProviders"][number]) {
  if (provider.state === "configured" && provider.canSend) {
    const sender = provider.senderSource === "tenant" ? "Tenant sender" : "Approved default sender";
    return { detail: `${provider.status.replaceAll("_", " ")} · ${sender}. Delivery requires receipt evidence.`, label: "Configured" };
  }
  if (provider.state === "canary_failed") {
    return { detail: "Sending is configured, but the latest delivery canary failed.", label: "Canary failed" };
  }
  if (provider.state === "degraded") {
    return { detail: "Sending needs review after a failed delivery or sender check.", label: "Needs attention" };
  }
  if (provider.channel === "sms") {
    return { detail: "SMS is disabled until a provider is configured and externally verified.", label: "Disabled" };
  }
  return { detail: "Email delivery is not configured.", label: "Unavailable" };
}

export function rankCentralDashboardWork(
  summary: Pick<CentralDashboardSummary, "attention" | "nextActionQueues">,
): CentralDashboardWorkItem[] {
  const priorityRank = { Critical: 0, High: 1, Normal: 2 } as const;
  const specificActions = summary.attention.map((item) => ({
      actionType: item.type,
      assignedTo: item.assignedTo,
      caseName: item.caseName,
      count: Math.max(1, item.linkedCount),
      detail: item.detail,
      href: item.href,
      id: `attention:${item.id}`,
      sourceId: item.id,
      receivedAt: item.receivedAt,
      updatedAt: item.updatedAt,
      dueAt: item.dueAt,
      version: item.version,
      kind: "action" as const,
      priority: item.priority,
      title: item.title,
    }));
  const queueFallbacks = summary.nextActionQueues.filter((queue) => queue.count > 0).map((queue) => ({
      assignedTo: queue.assignedTo,
      count: queue.count,
      detail: queue.description,
      href: queue.href,
      id: `queue:${queue.id}`,
      receivedAt: null,
      updatedAt: null,
      kind: "queue" as const,
      priority: queue.priority,
      title: queue.title,
    }));
  return (specificActions.length ? specificActions : queueFallbacks).sort((left, right) => (
    dashboardWorkMillis(right.updatedAt ?? right.receivedAt) - dashboardWorkMillis(left.updatedAt ?? left.receivedAt)
    || priorityRank[left.priority] - priorityRank[right.priority]
    || (left.kind === right.kind ? 0 : left.kind === "action" ? -1 : 1)
    || left.title.localeCompare(right.title)
  ));
}

function dashboardWorkMillis(value: string | null | undefined) {
  const timestamp = value ? new Date(value).getTime() : 0;
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

export function buildCentralDashboardDailyExport(summary: CentralDashboardSummary, now = new Date()) {
  const rows = summary.attention.map((item) => [
    now.toISOString().slice(0, 10),
    item.priority,
    item.title,
    item.assignedTo,
    item.linkedCount,
    item.caseId,
    item.detail,
    item.href,
  ]);
  const header = ["Date", "Priority", "Next action", "Assigned to", "Linked actions", "Case ID", "Detail", "Central link"];
  return {
    contents: [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\n"),
    fileName: `central-daily-actions-${now.toISOString().slice(0, 10)}.csv`,
  };
}

function csvCell(value: unknown) {
  const plain = String(value ?? "");
  const safe = /^[=+\-@]/.test(plain) ? `'${plain}` : plain;
  return `"${safe.replace(/"/g, '""')}"`;
}
